import React, { useMemo, useState } from "react";
import {
  CheckCircle2, Star, BarChart3, Folder, Archive,
  Plus, Search, ChevronLeft, ChevronRight, X, RotateCcw, GitCompare,
} from "lucide-react";

/**
 * CasesSidebar — collapsible left rail that replaces the horizontal
 * tabs strip inside AutoSimWorkspace. Groups cases by:
 *   • ★ Design Case (always one)
 *   • Pinned (engineer's 2-3 favourites)
 *   • Sensitivity sweeps (name contains 'sweep' or 'sensitivity')
 *   • Other (active)
 *   • Archived (collapsed by default)
 *
 * Includes a search input and is collapsible to icons-only mode for
 * engineers who want maximum canvas width.
 *
 * Kept in its own file so the AutoSimWorkspace.jsx tree stays shallow
 * (Babel's recursive AST walker has crashed on deeply-nested files
 * before — see handoff "Maximum Call Stack Exceeded" recurrence).
 */
const SENSITIVITY_NAME_RX = /(sweep|sensitivity)/i;

export function classifyCase(c) {
  if (c.archived) return "archived";
  if (c.is_design_case) return "design";
  if (c.pinned) return "pinned";
  if (c.is_sensitivity_sweep || SENSITIVITY_NAME_RX.test(c.name || "")) return "sensitivity";
  return "other";
}

const STATUS_COLORS = {
  draft: "#94a3b8", running: "#eab308",
  converged: "#22c55e", diverged: "#ef4444",
};

export default function CasesSidebar({
  cases, activeRun, onPick, onPin, onArchive, onUnarchive,
  onNewCase, onCompare, collapsed, onToggleCollapse,
}) {
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filterFn = (c) => !q || (c.name || "").toLowerCase().includes(q);
    const byKind = { design: [], pinned: [], sensitivity: [], other: [], archived: [] };
    for (const c of cases) {
      if (!filterFn(c)) continue;
      byKind[classifyCase(c)].push(c);
    }
    return byKind;
  }, [cases, query]);

  const archivedCount = cases.filter((c) => c.archived).length;
  const activeCount = cases.length - archivedCount;
  const SECTIONS = [
    { key: "design",      title: "Design Case",        icon: CheckCircle2, color: "#22c55e" },
    { key: "pinned",      title: "Pinned",             icon: Star,         color: "#fbbf24" },
    { key: "sensitivity", title: "Sensitivity sweeps", icon: BarChart3,    color: "#a855f7" },
    { key: "other",       title: "Other (active)",     icon: Folder,       color: "#94a3b8" },
  ];

  if (collapsed) {
    return (
      <div data-testid="autosim-cases-sidebar" data-collapsed="true" style={{
        width: 44, flexShrink: 0,
        background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
        borderRadius: 4, padding: "8px 6px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
      }}>
        <button data-testid="cases-sidebar-toggle" onClick={onToggleCollapse}
                title="Expand cases sidebar" style={iconBtn}>
          <ChevronRight size={14} />
        </button>
        <div style={{ width: "100%", height: 1, background: "var(--pf-border)" }} />
        <button data-testid="cases-sidebar-new-collapsed" onClick={onNewCase}
                title="New case" style={iconBtn}>
          <Plus size={14} />
        </button>
        <span style={{ fontSize: 9, color: "var(--pf-text-muted)", textAlign: "center" }}>
          {activeCount}<br/>active
        </span>
      </div>
    );
  }

  return (
    <div data-testid="autosim-cases-sidebar" data-collapsed="false" style={{
      width: 280, flexShrink: 0,
      background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
      borderRadius: 4, padding: 10, display: "flex", flexDirection: "column",
      gap: 8, maxHeight: "78vh", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className="pf-overline" style={{ color: "var(--pf-text-muted)", flex: 1 }}>
          CASES · {activeCount} active{archivedCount ? ` · ${archivedCount} archived` : ""}
        </span>
        <button data-testid="cases-sidebar-toggle" onClick={onToggleCollapse}
                title="Collapse sidebar" style={iconBtn}>
          <ChevronLeft size={13} />
        </button>
      </div>

      {/* Search + actions */}
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }}>
          <Search size={11} style={{ position: "absolute", left: 7, top: 7, color: "var(--pf-text-muted)" }} />
          <input
            data-testid="cases-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search cases…"
            className="pf-input pf-mono"
            style={{
              width: "100%", padding: "5px 6px 5px 22px", fontSize: 11,
              background: "var(--pf-surface)", color: "var(--pf-text)",
              border: "1px solid var(--pf-border)", borderRadius: 3,
            }}
          />
          {query && (
            <button onClick={() => setQuery("")} style={{
              position: "absolute", right: 4, top: 4, padding: 2,
              background: "transparent", border: 0, cursor: "pointer",
              color: "var(--pf-text-muted)",
            }}><X size={11} /></button>
          )}
        </div>
        <button data-testid="cases-sidebar-new" onClick={onNewCase}
                title="New case" style={iconBtn}>
          <Plus size={13} />
        </button>
        {activeCount >= 2 && (
          <button data-testid="cases-sidebar-compare" onClick={onCompare}
                  title="Compare cases" style={iconBtn}>
            <GitCompare size={13} />
          </button>
        )}
      </div>

      {/* Scroll container */}
      <div style={{ overflowY: "auto", flex: 1, paddingRight: 2 }}>
        {SECTIONS.map((sec) => {
          const items = groups[sec.key] || [];
          if (items.length === 0) return null;
          return (
            <Section key={sec.key} title={sec.title} Icon={sec.icon} color={sec.color}
                     count={items.length} testid={`cases-section-${sec.key}`}>
              {items.map((c) => (
                <CaseRow key={c.run_id} c={c} active={c.run_id === activeRun}
                  onPick={() => onPick(c.run_id)}
                  onPin={() => onPin(c.run_id)}
                  onArchive={() => onArchive(c.run_id, c.name)} />
              ))}
            </Section>
          );
        })}

        {archivedCount > 0 && (
          <div style={{ marginTop: 6 }}>
            <button
              data-testid="cases-toggle-archived"
              onClick={() => setShowArchived((v) => !v)}
              style={{
                width: "100%", display: "flex", alignItems: "center",
                gap: 6, padding: "5px 4px", fontSize: 10.5,
                color: "var(--pf-text-muted)", background: "transparent",
                border: 0, cursor: "pointer",
                borderTop: "1px dashed var(--pf-border)",
              }}
            >
              <Archive size={11} />
              <span style={{ flex: 1, textAlign: "left", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Archived ({archivedCount})
              </span>
              {showArchived ? <ChevronLeft size={10} style={{ transform: "rotate(-90deg)" }} />
                            : <ChevronRight size={10} style={{ transform: "rotate(90deg)" }} />}
            </button>
            {showArchived && (groups.archived || []).map((c) => (
              <ArchivedRow key={c.run_id} c={c} active={c.run_id === activeRun}
                onPick={() => onPick(c.run_id)}
                onUnarchive={() => onUnarchive(c.run_id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const iconBtn = {
  padding: "4px 5px", fontSize: 10, display: "inline-flex",
  alignItems: "center", justifyContent: "center",
  background: "transparent", color: "var(--pf-text-muted)",
  border: "1px solid var(--pf-border)", borderRadius: 3, cursor: "pointer",
};

function Section({ title, Icon, color, count, children, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 8 }}>
      <div className="pf-overline" style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "4px 4px", color, fontSize: 9.5,
        textTransform: "uppercase", letterSpacing: 0.6,
      }}>
        <Icon size={10} />
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ color: "var(--pf-text-muted)" }}>{count}</span>
      </div>
      {children}
    </div>
  );
}

function CaseRow({ c, active, onPick, onPin, onArchive }) {
  return (
    <div
      data-testid={`autosim-tab-${c.run_id}`}
      onClick={onPick}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 8px", marginBottom: 2, borderRadius: 3,
        background: active ? "var(--pf-spark)" : "transparent",
        color: active ? "#0a0e14" : "var(--pf-text)",
        border: c.is_design_case ? "1.5px solid #22c55e" : "1px solid transparent",
        cursor: "pointer", fontSize: 11.5,
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: STATUS_COLORS[c.status] || STATUS_COLORS.draft,
        flexShrink: 0,
      }} title={c.status} />
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={c.name}>{c.name}</span>
      {!c.is_design_case && (
        <>
          <button
            data-testid={`autosim-pin-${c.run_id}`}
            onClick={(e) => { e.stopPropagation(); onPin(); }}
            title={c.pinned ? "Unpin" : "Pin to top"}
            style={{
              padding: 2, background: "transparent", border: 0, cursor: "pointer",
              color: c.pinned ? "#fbbf24" : (active ? "rgba(10,14,20,0.55)" : "var(--pf-text-muted)"),
              display: "inline-flex", alignItems: "center",
            }}
          ><Star size={11} fill={c.pinned ? "#fbbf24" : "none"} /></button>
          <button
            data-testid={`autosim-delete-${c.run_id}`}
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            title="Archive"
            style={{
              padding: 2, background: "transparent", border: 0, cursor: "pointer",
              color: active ? "rgba(10,14,20,0.55)" : "var(--pf-text-muted)",
              display: "inline-flex", alignItems: "center",
            }}
          ><X size={11} /></button>
        </>
      )}
    </div>
  );
}

function ArchivedRow({ c, active, onPick, onUnarchive }) {
  return (
    <div
      data-testid={`autosim-tab-${c.run_id}`}
      onClick={onPick}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "5px 8px", marginBottom: 2, borderRadius: 3,
        background: active ? "var(--pf-spark)" : "transparent",
        color: active ? "#0a0e14" : "var(--pf-text-muted)",
        border: "1px dashed var(--pf-border)", cursor: "pointer",
        fontSize: 11, fontStyle: "italic", opacity: 0.8,
      }}
    >
      <Archive size={9} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={c.name}>{c.name}</span>
      <button
        data-testid={`autosim-unarchive-${c.run_id}`}
        onClick={(e) => { e.stopPropagation(); onUnarchive(); }}
        title="Restore"
        style={{
          padding: "2px 4px", background: "transparent", border: 0, cursor: "pointer",
          color: active ? "rgba(10,14,20,0.7)" : "var(--pf-text-muted)",
          fontSize: 9, display: "inline-flex", alignItems: "center", gap: 2,
        }}
      ><RotateCcw size={9} /> Restore</button>
    </div>
  );
}
